/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @atlaskit/ui-styling-standard/enforce-style-prop */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import type { HTMLAttributes, ReactNode } from 'react';
import { forwardRef, useEffect, useRef } from 'react';

import { cssMap, css as cssUnbounded, keyframes } from '@compiled/react';
import FocusLock from 'react-focus-lock';

import { css, jsx } from '@atlaskit/css';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { findOverflowScrollParent } from '@atlaskit/editor-common/ui';
import { fg } from '@atlaskit/platform-feature-flags';
import { token } from '@atlaskit/tokens';

import type { PaddingAdjustments } from './types';

type Props = {
	appearance?: EditorAppearance;
	children: ReactNode;
	disablePadding?: boolean;
	enableMaxHeight?: boolean;
	enableMaxWidth?: boolean;
	isAIFC?: boolean;
	paddingAdjustments?: PaddingAdjustments;
	rainbowBorder?: boolean;
	rainbowBorderAnimation?: boolean;
} & HTMLAttributes<HTMLDivElement>;

const isFirefox: boolean =
	typeof navigator !== 'undefined' && navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const PRISM_BORDER_STEP_1 = '#0065FF';
const PRISM_BORDER_STEP_2 = '#0469FF';
const PRISM_BORDER_STEP_3 = '#BF63F3';
const PRISM_BORDER_STEP_4 = '#FFA900';

const defaultPadding = { top: 12, bottom: 12, left: 12, right: 12 };

// ED-20633, DSP-14045: Avoid space tokens for now as space tokens are currently using rem units
// and Safari has a bug where rem is incorrectly calculated when there are
// animations involved
// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const prismBorderStyles = cssUnbounded({
	'@property --gradient-angle': { syntax: "'<angle>'", initialValue: '270deg', inherits: 'false' },
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
	borderRadius: token('radius.large'),
	boxShadow: 'none',
	'&::before, &::after': {
		content: "''",
		position: 'absolute',
		zIndex: -1,
		width: 'calc(100% + 2px)',
		height: 'calc(100% + 2px)',
		top: '-1px',
		left: '-1px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
		borderRadius: `calc(${token('radius.large')} + 1px)`,
		transform: 'translate3d(0, 0, 0)',
	},
	'&::after': { right: '1px', bottom: '1px', filter: 'blur(8px)', opacity: 0.5 },
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

const newPrismBorderStyles = cssUnbounded({
	'@property --gradient-angle': { syntax: "'<angle>'", initialValue: '270deg', inherits: 'false' },
	position: 'relative',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
	borderRadius: '1rem',
	boxShadow: 'none',
	'&::before, &::after': {
		content: "''",
		position: 'absolute',
		zIndex: -1,
		width: 'calc(100% + 2px)',
		height: 'calc(100% + 2px)',
		top: '-1px',
		left: '-1px',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
		borderRadius: `calc(1rem + 1px)`,
		transform: 'translate3d(0, 0, 0)',
	},
	'&::after': { right: '1px', bottom: '1px', filter: 'blur(8px)', opacity: 0.5 },
	'@media (prefers-reduced-motion)': { animation: 'none' },
});

const staticStyles = cssMap({
	default: {
		'&::before, &::after': {
			background: `conic-gradient(
				from var(--gradient-angle, 270deg),
				${PRISM_BORDER_STEP_1} 0%,
				${PRISM_BORDER_STEP_2} 20%,
				${PRISM_BORDER_STEP_3} 50%,
				${PRISM_BORDER_STEP_4} 56%,
				${PRISM_BORDER_STEP_1} 100%
			)`,
		},
	},
	firefox: {
		'&::before, &::after': {
			background: `linear-gradient(90deg,
			${PRISM_BORDER_STEP_1} 0%,
			${PRISM_BORDER_STEP_2} 12%,
			${PRISM_BORDER_STEP_3} 24%,
			${PRISM_BORDER_STEP_4} 48%,
			${PRISM_BORDER_STEP_3} 64%,
			${PRISM_BORDER_STEP_2} 80%,
			${PRISM_BORDER_STEP_1} 100%
			)`,
			backgroundSize: '200%',
		},
	},
});

const fireFoxBgPosition = keyframes({
	from: { backgroundPosition: '100%' },
	to: { backgroundPosition: '-100%' },
});

const rotationAnimation = keyframes({
	// @ts-ignore - error TS2769: No overload matches this call.
	from: { '--gradient-angle': '0deg' },
	// @ts-ignore - error TS2769: No overload matches this call.
	to: { '--gradient-angle': '360deg' },
});

const animationStyles = cssMap({
	default: {
		'&::before, &::after': {
			animationName: rotationAnimation,
			animationDuration: '2s',
			animationTimingFunction: 'linear',
			animationIterationCount: 'infinite',
		},
	},
	firefox: {
		'&::before, &::after': {
			animationName: fireFoxBgPosition,
			animationDuration: '1s',
			animationTimingFunction: 'linear',
			animationIterationCount: 'infinite',
			animationDirection: 'normal',
		},
	},
});

// eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage
const constantStyles = cssUnbounded({
	boxShadow: token('elevation.shadow.overlay'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/platform/no-module-level-eval
	borderRadius: token('radius.large'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'> :last-child': { borderBottomLeftRadius: 'inherit', borderBottomRightRadius: 'inherit' },
	height: '100%',
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface.overlay'),
});

const newConstantStyles = cssUnbounded({
	boxShadow: token('elevation.shadow.overlay'),
	borderRadius: '1rem',
	// don't define height here as it will affect the unhandled error modal height if it's caught by the outer error boundary (there are two error boundaries in AI...)
	width: '100%',
	boxSizing: 'border-box',
	backgroundColor: token('elevation.surface.overlay'),
});

const maxHeightCSS = css({ maxHeight: '500px' });

const maxWidthCSS = css({
	width: '400px',
	maxWidth: '400px',
	justifySelf: 'center',
});

const chromelessWidth = {
	width: `calc(100% - ${token('space.300')})`,
	justifySelf: 'center',
};

/**
 * FocusLock adds a focus trap so that users can keep tabbing through the
 * command palette without leaving it unless when pressing escape or clicking outside.
 * Fix this up when cleaning up editor_a11y_focus_trap_ai.
 */
const GatedFocusLock = ({ children, isAIFC }: { children: ReactNode; isAIFC?: boolean }) => {
	const scrollParentRef = useRef<HTMLElement>();
	const ref = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (ref.current) {
			scrollParentRef.current = findOverflowScrollParent(ref.current) || undefined;
		}
	}, []);
	if (!isAIFC && fg('editor_a11y_focus_trap_ai')) {
		return (
			<FocusLock
				ref={ref}
				css={{ width: '100%' }}
				noFocusGuards
				returnFocus={{ preventScroll: true }}
				whiteList={(activeElement) => {
					if (activeElement === scrollParentRef.current) {
						return false;
					}
					return true;
				}}
				/**
				 * focusOptions is deprecated but based on docs, it can still be used.
				 * We need to prevent scroll when auto-focusing to prevent issues with
				 * modal positioning.
				 * Modal will now always be on screen - it will be docked to top or bottom if
				 * it is out of viewport.
				 */
				focusOptions={{ preventScroll: true }}
			>
				{children}
			</FocusLock>
		);
	}
	return children;
};

export const FloatingContainer = forwardRef<HTMLDivElement, Props>(
	(
		{
			appearance,
			paddingAdjustments,
			rainbowBorder = true,
			rainbowBorderAnimation,
			disablePadding,
			children,
			enableMaxHeight,
			enableMaxWidth,
			className,
			style,
			isAIFC,
			...rest
		}: Props,
		ref: React.Ref<HTMLDivElement>,
	) => {
		return (
			<GatedFocusLock isAIFC={isAIFC}>
				<div
					ref={ref}
					css={[
						rainbowBorder && !isAIFC && prismBorderStyles,
						rainbowBorder && isAIFC && newPrismBorderStyles,
						staticStyles[isFirefox ? 'firefox' : 'default'],
						isAIFC ? newConstantStyles : constantStyles,
						rainbowBorderAnimation && animationStyles[isFirefox ? 'firefox' : 'default'],
						// maxHeight for command palette (especially when there are lots of user input)
						// It is a reasonable number for tickets EDF-1452, EDF-1572, EDF-1566
						// If you update this value, please also update the maxHeight value in Suggestions.tsx and test file editor-plugin-ai/src/__tests__/playwright/suggestions/scrollbar.spec.ts
						// when promptBuilder is removed, perhaps move this into css to keep it as a default style
						enableMaxHeight ? maxHeightCSS : null,
						enableMaxWidth ? maxWidthCSS : null,
					]}
					style={{
						...style,
						// Apply chromeless width styles inline to ensure precedence over CSS classes for screen components (Discard, NoImprovement, etc.)
						...(appearance === 'chromeless' && isAIFC ? chromelessWidth : null),
						...(disablePadding
							? null
							: paddingAdjustments
								? {
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingTop: `${defaultPadding.top + (paddingAdjustments.top ?? 0)}px`,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingRight: `${defaultPadding.right + (paddingAdjustments.right ?? 0)}px`,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingBottom: `${defaultPadding.bottom + (paddingAdjustments.bottom ?? 0)}px`,
										// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
										paddingLeft: `${defaultPadding.left + (paddingAdjustments.left ?? 0)}px`,
									}
								: { padding: `${defaultPadding.top}px ${defaultPadding.left}px` }),
					}}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
					className={className}
					{...rest}
				>
					{children}
				</div>
			</GatedFocusLock>
		);
	},
);
